import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrutalistBadge, BrutalistCard } from './BrutalistUI';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const DataField = ({ label, value, icon, mono = false, highlight = false, customStyle = {} }) => (
  <div 
    className={`mb-6 last:mb-0 ${highlight ? 'bg-black text-white p-5 border-4 border-black shadow-[4px_4px_0px_0px_rgba(255,255,255,1)]' : ''}`}
    style={customStyle}
  >
    <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${highlight ? 'text-white/70' : 'opacity-60'}`}>
      <SafeIcon icon={icon} className="text-sm" /> {label}
    </h3>
    <p className={`text-base sm:text-lg leading-relaxed font-bold ${mono ? 'font-mono bg-[#F4F0EB] p-4 border-2 border-black text-black text-sm shadow-inner' : ''}`}>
      {value || 'Not provided'}
    </p>
  </div>
);

const DetailModal = ({ term, onClose, onEdit, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('meaning'); // 'meaning', 'usage', 'details'

  useEffect(() => {
    if (term) {
      document.body.style.overflow = 'hidden';
      setActiveTab('meaning'); // Reset to first tab on open
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [term]);

  if (!term) return null;

  const getFontSizeClass = (text) => {
    const len = text.length;
    if (len <= 12) return 'text-4xl sm:text-5xl';
    if (len <= 18) return 'text-3xl sm:text-4xl';
    return 'text-2xl sm:text-3xl';
  };

  const handleDelete = async () => {
    if (window.confirm(`PERMANENTLY DELETE "${term.term}"? THIS ACTION CANNOT BE UNDONE.`)) {
      setIsDeleting(true);
      try {
        await onDelete(term.id);
        onClose();
      } catch (error) {
        alert('DELETE FAILED: ' + error.message);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
      >
        <motion.div 
          initial={{ y: 50, opacity: 0, scale: 0.95 }} 
          animate={{ y: 0, opacity: 1, scale: 1 }} 
          exit={{ y: 20, opacity: 0, scale: 0.95 }} 
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-[600px] h-full max-h-[85vh] flex flex-col" 
          onClick={(e) => e.stopPropagation()}
        >
          <BrutalistCard color={term.color} className="flex flex-col h-full !p-0 overflow-hidden shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] border-4">
            
            {/* Modal Header - Fixed */}
            <div className="bg-black text-white p-6 pt-12 border-b-4 border-black relative shrink-0">
              <div className="absolute top-4 right-4 flex gap-2 z-10">
                <button 
                  onClick={() => onEdit(term)} 
                  className="bg-white text-black p-2 border-2 border-black hover:bg-yellow-400 transition-colors shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] active:translate-y-0.5 active:shadow-none"
                  title="Edit Term"
                >
                  <SafeIcon icon={FiIcons.FiEdit3} className="text-sm" />
                </button>
                <button 
                  onClick={handleDelete} 
                  disabled={isDeleting}
                  className="bg-white text-black p-2 border-2 border-black hover:bg-red-500 transition-colors shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] active:translate-y-0.5 active:shadow-none"
                  title="Delete Term"
                >
                  <SafeIcon icon={isDeleting ? FiIcons.FiRefreshCw : FiIcons.FiTrash2} className={`text-sm ${isDeleting ? 'animate-spin' : ''}`} />
                </button>
                <button 
                  onClick={onClose} 
                  className="bg-white text-black p-2 border-2 border-black hover:bg-gray-200 transition-colors shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] active:translate-y-0.5 active:shadow-none"
                  title="Close"
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-sm" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <BrutalistBadge variant="white" className="!px-3 !py-1 !text-[10px]">{term.category}</BrutalistBadge>
                <span className="border-2 border-white px-3 py-1 font-mono font-black text-[10px] bg-transparent text-white uppercase tracking-wider">
                  Ref: {term.dateAdded}
                </span>
              </div>

              <h1 className={`${getFontSizeClass(term.term)} font-black tracking-tighter mb-2 whitespace-nowrap overflow-hidden text-ellipsis`}>
                {term.term}
              </h1>
            </div>

            {/* Tab Navigation - Fixed */}
            <div className="flex border-b-4 border-black bg-[#F4F0EB] shrink-0">
              {['meaning', 'usage', 'details'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 text-[11px] font-black uppercase tracking-widest border-r-4 border-black last:border-r-0 transition-colors
                    ${activeTab === tab ? 'bg-white shadow-[inset_0px_4px_0px_0px_rgba(0,0,0,1)]' : 'opacity-50 hover:bg-black/5 hover:opacity-100'}
                  `}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Scrollable Content Body */}
            <div className="p-6 bg-white overflow-y-auto flex-grow">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* TAB 1: MEANING */}
                  {activeTab === 'meaning' && (
                    <div className="space-y-6">
                      <DataField label="The Essence" value={term.plainEnglish} icon={FiIcons.FiZap} />
                      {term.simpleWay && (
                        <DataField label="In Simple Terms" value={term.simpleWay} icon={FiIcons.FiMessageSquare} highlight />
                      )}
                      {term.spanishSummary && (
                        <DataField 
                          label="Spanish Translation" 
                          value={term.spanishSummary} 
                          icon={FiIcons.FiGlobe} 
                          customStyle={{ 
                            backgroundColor: '#9EA1D4', 
                            color: '#000', 
                            padding: '20px', 
                            border: '4px solid #000',
                            boxShadow: '4px 4px 0px 0px #000'
                          }} 
                        />
                      )}
                    </div>
                  )}

                  {/* TAB 2: USAGE */}
                  {activeTab === 'usage' && (
                    <div className="space-y-6">
                      <DataField label="Real World Usage" value={term.exampleSentence} icon={FiIcons.FiCode} mono />
                      {term.context && (
                        <DataField label="Operational Context" value={term.context} icon={FiIcons.FiCpu} />
                      )}
                    </div>
                  )}

                  {/* TAB 3: DETAILS */}
                  {activeTab === 'details' && (
                    <div className="space-y-8">
                      <div className="p-5 border-4 border-black bg-[#F4F0EB] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <h3 className="text-[10px] font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiIcons.FiHash} /> Related Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {Array.isArray(term.keyTerms) && term.keyTerms.length > 0 ? term.keyTerms.map((tag, idx) => (
                            <span key={idx} className="bg-black text-white text-[10px] font-black px-3 py-1 uppercase tracking-tighter border-2 border-black hover:bg-white hover:text-black transition-colors cursor-default">
                              {tag}
                            </span>
                          )) : (
                            <span className="text-xs font-bold opacity-50">No tags provided</span>
                          )}
                        </div>
                      </div>

                      {term.sourceUrl && (
                        <a 
                          href={term.sourceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center justify-between p-4 border-4 border-black bg-white hover:bg-yellow-400 transition-colors shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] group"
                        >
                          <div className="flex items-center gap-3">
                            <SafeIcon icon={FiIcons.FiLink} className="text-xl" />
                            <div>
                              <p className="text-[10px] font-black uppercase opacity-60 mb-0.5">Source Material</p>
                              <p className="text-sm font-bold truncate max-w-[200px] sm:max-w-[300px]">{term.sourceTitle || term.sourceUrl}</p>
                            </div>
                          </div>
                          <SafeIcon icon={FiIcons.FiExternalLink} className="text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </a>
                      )}

                      <div className="pt-6 flex flex-col gap-1 opacity-40 border-t-2 border-black border-dashed">
                        <p className="font-mono text-[9px] font-black uppercase">
                          ID_LOC: {term.id}
                        </p>
                        <p className="font-mono text-[9px] font-black uppercase">
                          TS_UPD: {new Date(term.updated_at || term.created_at).toISOString()}
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </BrutalistCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DetailModal;